import { motion } from "framer-motion";
import { ShieldCheck, Wifi, MonitorSmartphone } from "lucide-react";
import { HERO_STATS } from "@/lib/salesData";

/* HeroSection — Seção principal de impacto e conversão */
export const HeroSection = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center bg-background py-16 sm:py-20 lg:py-24 px-4 overflow-hidden">
      {/* Glow radial de fundo */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-accent/15 via-transparent to-transparent pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[300px] sm:w-[500px] lg:w-[700px] h-[300px] sm:h-[500px] lg:h-[700px] bg-primary/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto max-w-7xl relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

          {/* LADO ESQUERDO — copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="space-y-5 sm:space-y-7 text-center lg:text-left"
          >
            {/* Badge de posicionamento */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-semibold">
              🏥 Plataforma Clínica de Enfermagem Oncológica
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
              Jornada Oncológica{" "}
              <span className="text-accent">na Prática.</span>
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg mx-auto lg:mx-0">
              A plataforma de consulta clínica para enfermeiros oncológicos que precisam de{" "}
              <strong className="text-foreground">respostas rápidas e seguras</strong> — módulos, calculadoras, protocolos e quizzes no celular, prontos para o plantão.
            </p>

            {/* Mini badges de benefício */}
            <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground bg-card border border-border px-3 py-1.5 rounded-full">
                <Wifi className="w-3.5 h-3.5 text-accent" /> Acesso Online
              </span>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground bg-card border border-border px-3 py-1.5 rounded-full">
                <MonitorSmartphone className="w-3.5 h-3.5 text-accent" /> Fácil Acesso
              </span>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground bg-card border border-border px-3 py-1.5 rounded-full">
                <ShieldCheck className="w-3.5 h-3.5 text-accent" /> 7 dias de garantia
              </span>
            </div>

            {/* Stats em grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {HERO_STATS.slice(0, 4).map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.07 }}
                  className="bg-card border border-border rounded-xl px-2 sm:px-4 py-2 sm:py-3 text-center"
                >
                  <div className="font-extrabold text-accent font-mono text-lg sm:text-xl">{stat.value}</div>
                  <div className="text-muted-foreground text-xs mt-0.5 leading-tight">{stat.label}</div>
                </motion.div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <motion.a
                href="https://pay.kiwify.com.br/bGcYJms"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-accent hover:bg-accent/90 text-accent-foreground text-sm sm:text-base font-extrabold px-6 sm:px-7 py-4 rounded-xl transition-colors text-center cursor-pointer w-full sm:w-auto"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                animate={{
                  boxShadow: [
                    "0px 0px 0px 0px rgba(104,211,145,0)",
                    "0px 0px 20px 4px rgba(104,211,145,0.3)",
                    "0px 0px 0px 0px rgba(104,211,145,0)",
                  ],
                }}
                transition={{ boxShadow: { repeat: Infinity, duration: 2.5 } }}
              >
                Quero acesso agora →
              </motion.a>
              <button
                onClick={() => scrollToSection("modules")}
                className="border border-accent/30 text-accent hover:bg-accent/10 text-sm sm:text-base font-semibold px-6 py-4 rounded-xl transition-colors w-full sm:w-auto"
              >
                Explorar conteúdo ↓
              </button>
            </div>
          </motion.div>

          {/* LADO DIREITO — mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative mt-6 lg:mt-0"
          >
            <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-3xl pointer-events-none" />
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
              <img
                src="/images/hero_dashboard_20260425.jpg"
                alt="Dashboard da Plataforma Jornada Oncológica na Prática"
                className="w-full h-auto object-contain"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
